import React, { type FC, useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import noop from 'lodash/noop';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

import { Spinner } from 'components/Shared/Spinner';
import { Button } from 'components/Shared/Inputs';
import { CIReferralStatus } from 'components/CriticalIncident/interfaces';
import { isExpiredReferral } from 'components/CriticalIncident/utils/CIReferralUtils';
import { useAcceptCriticalIncidentReferral, useDeclineCriticalIncidentReferral, useGetCriticalIncidentReferral } from 'api/queries/criticalIncidentQuery';
import { useDialogShadow } from 'hooks/useDialogShadow';

import { CIHighLevelDetails } from '../CIHighLevelDetails';
import { DeclineCIReferral } from '../DeclineCIReferral';
import { AcceptCIReferral } from '../AcceptCIReferral';
import { UnavailableCIReferral } from '../UnavailableCIReferral';
 
import "./CIReferralDecisionModal.scss";

interface CIReferralDecisionModalProps {
    open: boolean;
    onClose: () => void;
    referralId: string;
    providerId: number;
}

type ReferralState = 'Accepted' | 'Declined' | 'Unavailable' | 'Pending'| 'Loading';

export const CIReferralDecisionModal: FC<CIReferralDecisionModalProps> = (props) => {
    const { open, onClose, referralId, providerId } = props;
    const { data: referral, error } = useGetCriticalIncidentReferral(referralId);
    const { mutateAsync: acceptReferral, isPending: accepting } = useAcceptCriticalIncidentReferral();
    const { mutateAsync: declineReferral, isPending: declining } = useDeclineCriticalIncidentReferral();
    const queryClient = useQueryClient();
    const [referralState, setReferralState] = useState<ReferralState>('Pending');
    const [declineReason, setDeclineReason] = useState<string>('');
    const [isMutated, setIsMutated] = useState<boolean>(false);
    const { modalRef, headerRef, footerRef, topShadowTriggerRef, bottomShadowTriggerRef, isHeaderIntersecting, isFooterIntersecting } = useDialogShadow();

    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });

    const onBackClick = () => {
        queryClient.invalidateQueries({ queryKey: ['criticalIncident', 'referral', referral?.referralId] });
        setReferralState('Pending');
    };


    const modalContent = useMemo(() => {
        switch (referralState) {
            case 'Pending':
                return !!referral ? <CIHighLevelDetails referral={referral!} /> : <Spinner />;
            case 'Declined':
                return !!referral ? <DeclineCIReferral reason={declineReason} onReasonChange={setDeclineReason} /> : <Spinner />;
            case 'Accepted':
                return !!referral ? <AcceptCIReferral onBackClick={onBackClick} /> : <Spinner />;
            case 'Unavailable':
                return <UnavailableCIReferral />;
            case 'Loading':
            default:
                return <Spinner />;
        }
    }, [referralState, referral, declineReason, setDeclineReason]);

    if (!!referral && isExpiredReferral(referral)) {
        referral.status = CIReferralStatus.Expired;
    }

    const handleAccept = () => {
        if (referral?.referralId && providerId) {
            acceptReferral(referral?.referralId)
                .then(() => {
                    setIsMutated(true);
                    setReferralState('Accepted');
                }).catch((err) => {
                    if (err?.code) {
                        setReferralState('Unavailable');
                    }
                    console.error(err);
                });
        }
    };

    const handleDecline = () => {
        if (referral?.referralId && declineReason) {
            declineReferral({ 
                id: referral?.referralId, 
                reason: declineReason,
            }).then(() => {
                closeAndReloadGrid();
            }).catch((err) => {
                if (err?.code) {
                    setReferralState('Unavailable');
                } else {
                    setReferralState('Declined');
                }
                console.error(err);
            });
        }
    };

    const handleClose = () => {
        isMutated && queryClient.invalidateQueries({ queryKey: ['criticalIncident', 'referrals'] });
        onClose();
        setDeclineReason('');
        setReferralState('Pending');
        setIsMutated(false);
    };

    const closeAndReloadGrid = () => {
        handleClose();
        queryClient.invalidateQueries({ queryKey: ['criticalIncident', 'referrals'] });
        queryClient.invalidateQueries({ queryKey: ['criticalIncident', 'referral', referral?.referralId] });
    }

    useEffect(() => {
        if (accepting || declining) {
            setReferralState('Loading');
        }
    }, [accepting, declining]);

    useEffect(() => {
        if (error) {
            setReferralState('Unavailable');
        }
    }, [error]);

    useEffect(() => {
        if (open && (isExpiredReferral(referral) || referral?.status === CIReferralStatus.Expired)) {
            setIsMutated(true);
            setReferralState('Unavailable');
        }
    }, [referral, isExpiredReferral(referral), open]);

    const okButtonProps = useMemo(() => {
        const buttonProps = {
            children: t('i_am_interested'),
            onClick: handleAccept,
            busy: false,
            disabled: false,
        }
        switch(referralState) {
            case 'Accepted':
                buttonProps.children = t('view_case');
                buttonProps.onClick = noop;
                break;
            case 'Declined':
                buttonProps.children = t('submit');
                buttonProps.onClick = handleDecline;
                buttonProps.disabled = !declineReason;
                break;
            case 'Loading':
                buttonProps.busy = true;
                break;
            default:
                buttonProps.children = t('i_am_interested');
                buttonProps.onClick = handleAccept;
        }
        return buttonProps;
    }, [referralState, declineReason, translationsReady, referral]);

    const cancelButtonProps = useMemo(() => {
        const buttonProps = {
            children: t('decline'),
            onClick: () => setReferralState('Declined'),
            variant: 'text',
            busy: false,
            disabled: false,
        }
        switch(referralState) {
            case 'Pending':
                buttonProps.children = t((!referral || isExpiredReferral(referral) || referral?.status === CIReferralStatus.Accepted ? 'close' : 'decline'));
                buttonProps.onClick = () => {
                    if (referral?.status === CIReferralStatus.Open && !isExpiredReferral(referral)) {
                        setReferralState('Declined');
                    } else {
                        handleClose();
                    }
                };
                break;
            case 'Loading':
                buttonProps.disabled = true;
                break;
            case 'Accepted':
            case 'Declined':
            case 'Unavailable':
            default:
                buttonProps.children = t('close');
                buttonProps.onClick = isMutated ? closeAndReloadGrid : handleClose;
        }
        return buttonProps;
    }, [referralState, translationsReady, referral, isMutated]);

    const showOkButton = useMemo(() => {
        return referral?.status === CIReferralStatus.Open && !isExpiredReferral(referral) && !['Accepted', 'Unavailable'].includes(referralState);
    }, [referralState, referral]);

    const showCancelButton = useMemo(() => {
        return referral && !(referralState === 'Pending' && (isExpiredReferral(referral)));
    }, [referralState, referral]);

    return (
        <Modal open={open} ref={modalRef} onClose={handleClose} className='dialog_wrapper ci_referral_decision_dialog' role="dialog" aria-modal
            aria-labelledby="ci_referral_dialog_title" aria-describedby="ci_referral_dialog_content">
            <div className='dialog_surface'>
                <div ref={headerRef} className={clsx('dialog_title_wrapper', { 'top-shadow': !isHeaderIntersecting })}>
                    <h3 id="ci_referral_dialog_title">
                        {t('work_order_id_title', { workOrderId: referral?.externalWorkOrderId })}
                    </h3>
                    <IconButton aria-label={t('close')} onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className="dialog_content_wrapper" id="ci_referral_dialog_content">
                    <div ref={topShadowTriggerRef} className="shadow-trigger top">&nbsp;</div>
                    {modalContent}
                    <div ref={bottomShadowTriggerRef} className="shadow-trigger bottom">&nbsp;</div>
                </div>
                <div ref={footerRef} className={clsx('dialog_actions_wrapper', { 'bottom-shadow': !isFooterIntersecting })}>
                    {/* @ts-expect-error */}
                    {showCancelButton && <Button {...cancelButtonProps} />}
                    {showOkButton && <Button {...okButtonProps} />}
                </div>
            </div>
        </Modal>
    )
};
